import React, { useEffect, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStoreActions, useStoreState } from "easy-peasy";
import mixpanel from "mixpanel-browser";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import axios from "../../utils/axiosInst";
import Layout from "../layout/Layout";
import { useLocation } from "react-router-dom";
import ProviderCard from "./ProviderCard";
import LoadingScreen from "../../../../components/custom/LoadingScreen";
import Pagination from "../../../../components/common/Pagination";
import { ProvidersListV4, DentalProvidersListV4 } from "../../../../constants";
import { brand } from "../../../../config/brands";

const schema = yup
  .object({
    city: yup.string(),
    address: yup.string(),
    name: yup.string(),
    userType: yup.string(),
    ratings: yup.string(),
    reco: yup.string(),
    distance: yup.string(),
  })
  .required();

const Providers = () => {
  const getProviders = useStoreActions((actions) => actions.getProviders);
  const setFilterData = useStoreActions((actions) => actions.setFilterData);
  const providers = useStoreState((state) => state.providers);
  const location = useLocation();
  const loader = useStoreState((state) => state.loading);
  const filterData = useStoreState((state) => state.filterData);
  const [rating, setRating] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(true);
  const [distanceError, setDistanceError] = useState("");
  const [city, setCity] = useState([]);
  const [selectedCity, setSelectedCity] = useState(filterData?.address || "");
  const [selectedCityLat, setSelectedCityLat] = useState(filterData?.lat || "");
  const [selectedCityLng, setSelectedCityLng] = useState(filterData?.lng || "");
  const [search] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [locationPermissionGranted, setLocationPermissionGranted] =
    useState(false);
  const [isClearingFilters, setIsClearingFilters] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isPaginationNavigation, setIsPaginationNavigation] = useState(false);
  const { register, handleSubmit, setValue, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      address: filterData?.address || "",
      distance: filterData?.distance || "",
      name: filterData?.name || "",
      userType: filterData?.type || "",
      ratings: filterData?.ratings || "",
      mopRecommended: filterData?.mop || "",
    },
  });
  const distanceValue = watch("distance");
  const setProviderPagination = useStoreActions(
    (actions) => actions.setProviderPagination
  );
  const totalPages = useStoreState(
    (state) => state.providerPagination.totalPages
  );
  const currentPageNo = useStoreState(
    (state) => state.providerPagination.currentPageNo
  );
  const [formPreData] = useState({ limit: 10, page: 1 });

  // Reference for address input and suggestion list
  const addressInputRef = useRef(null);
  const suggestionListRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        addressInputRef.current &&
        suggestionListRef.current &&
        !addressInputRef.current.contains(event.target) &&
        !suggestionListRef.current.contains(event.target)
      ) {
        setCity([]);
      }
    };

    const handleScroll = () => {
      setCity([]);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const getCurrentLocation = (isAutomaticRequest = false) => {
    setIsGettingLocation(true);
    setLocationError("");
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser");
      setIsGettingLocation(false);
      if (!isAutomaticRequest) {
        setLocationError("Geolocation is not supported by this browser");
      }
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedCityLat(latitude);
        setSelectedCityLng(longitude);
        setLocationPermissionGranted(true);
        localStorage.setItem("mop-location-permission", "granted");
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
        fetch(url)
          .then((res) => res.json())
          .then((data) => {
            const address = data.display_name;
            setValue("address", address);
            setSelectedCity(address);
            if (address) {
              setValue("distance", "25");
            }
            setIsGettingLocation(false);
          })
          .catch((error) => {
            console.error("Error getting address:", error);
            const genericAddress = `Location (${latitude.toFixed(
              4
            )}, ${longitude.toFixed(4)})`;
            setValue("address", genericAddress);
            setSelectedCity(genericAddress);
            setValue("distance", "25");
            setIsGettingLocation(false);
          });
      },
      (error) => {
        console.error("Error getting location:", error);
        setIsGettingLocation(false);
        if (!isAutomaticRequest) {
          let errorMessage = "Unable to get your location.";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage =
                "Location access was denied. Please enable location services in your browser settings.";
              setLocationPermissionGranted(false);
              localStorage.setItem("mop-location-permission", "denied");
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage =
                "Location information is unavailable. Please try again or enter your address manually.";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out. Please try again.";
              break;
            default:
              errorMessage =
                "An unknown error occurred while getting your location.";
          }
          setLocationError(errorMessage);
        } else {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermissionGranted(false);
            localStorage.setItem("mop-location-permission", "denied");
          }
        }
        if (isAutomaticRequest) {
          getProviders({ page: 1 });
          setHasSearched(true);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  const handleDirectLocationAccess = () => {
    getCurrentLocation(false);
  };

  const selectCityHandler = (i) => {
    setValue("address", city[i].description);
    setSelectedCity(city[i].description);
    setSelectedCityLat(city[i].geometry.location.lat);
    setSelectedCityLng(city[i].geometry.location.lng);
    setCity([]);
    setDistanceError("");
    if (city[i].description && !distanceValue) {
      setValue("distance", "25");
    }
  };

  const onChangeCityHandler = (e) => {
    const value = e.target.value;
    setCity([]);
    setSelectedCityLat("");
    setSelectedCityLng("");
    setSelectedCity(value);
    setValue("address", value);
    if (value.length > 3) {
      axios.get(`/v1/public/search/address?place=${value}`).then(({ data }) => {
        setCity(data.predictions);
      });
    } else {
      setCity([]);
    }
    setDistanceError("");
    if (value && !distanceValue) {
      setValue("distance", "25");
    } else if (!value) {
      setValue("distance", "");
    }
  };

  const handlePageChange = (page) => {
    setIsPaginationNavigation(true);
    formPreData.page = page;
    if (filterData) {
      formPreData.name = filterData?.name || "";
      formPreData.userType = filterData?.type || "";
      formPreData.ratings = filterData?.ratings;
      formPreData.address = filterData?.address || "";
      formPreData.city = filterData?.city || "";
      formPreData.mopRecommended = filterData?.mop;
      formPreData.lat = filterData?.lat || "";
      formPreData.lng = filterData?.lng || "";
      formPreData.distance = filterData?.distance || "";
    }
    if (search) formPreData.search = search;
    let data = {
      name: filterData?.name || "",
      type: filterData?.type || "",
      ratings: filterData?.ratings || "",
      address: filterData?.address || "",
      city: filterData?.city || "",
      mop: filterData?.mop || "",
      lat: filterData?.lat || "",
      lng: filterData?.lng || "",
      page: page,
      back: true,
      distance: filterData?.distance || "",
      isSearchResult: filterData?.isSearchResult || false,
    };
    setFilterData(data);
    getProviders(formPreData);
  };

  // Initial load: Check sessionStorage and handle filter retention
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.getElementById("root").scrollTo(0, 0);
    const providerViewed = sessionStorage.getItem("providerViewed") === "true";

    if (providerViewed && filterData && Object.keys(filterData).length > 0) {
      setSelectedCity(filterData.address || "");
      setSelectedCityLat(filterData.lat || "");
      setSelectedCityLng(filterData.lng || "");
      Object.entries({
        userType: filterData?.type || "",
        name: filterData?.name || "",
        ratings: filterData?.ratings || "",
        address: filterData?.address || "",
        city: filterData?.city || "",
        mopRecommended: filterData?.mop || "",
        distance: filterData?.distance || "",
        page: filterData?.page || 1,
      }).forEach(([key, value]) => setValue(key, value));
      setRating(filterData.ratings || "");
      getProviders({
        page: filterData.page || 1,
        name: filterData.name || "",
        userType: filterData.type || "",
        ratings: filterData.ratings || "",
        address: filterData.address || "",
        city: filterData.city || "",
        mopRecommended: filterData.mop || "",
        lat: filterData.lat || "",
        lng: filterData.lng || "",
        distance: filterData.distance || "",
      });
      setHasSearched(true);
      sessionStorage.removeItem("providerViewed");
    } else {
      setFilterData({});
      setSelectedCityLat("");
      setSelectedCityLng("");
      setSelectedCity("");
      setRating("");
      Object.entries({
        userType: "",
        name: "",
        ratings: "",
        address: "",
        city: "",
        mopRecommended: "",
        distance: "",
      }).forEach(([key, value]) => setValue(key, value));
      const savedPermission = localStorage.getItem("mop-location-permission");
      if (savedPermission === "granted") {
        setLocationPermissionGranted(true);
        getCurrentLocation(true);
      } else if (savedPermission === "denied") {
        setLocationPermissionGranted(false);
        getProviders({ page: 1 });
      } else {
        getCurrentLocation(true);
      }
    }

    try {
      mixpanel.track("User", {
        type: "Page View",
        name: "Providers Page",
      });
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger search after location is available
  useEffect(() => {
    if (
      locationPermissionGranted &&
      selectedCityLat &&
      selectedCityLng &&
      !hasSearched &&
      !filterData?.isSearchResult
    ) {
      const formValues = watch();
      const searchData = {
        name: formValues.name || "",
        type: formValues.userType || "",
        ratings: rating,
        address: selectedCity,
        city: "",
        mop: formValues.mopRecommended || "",
        lat: selectedCityLat,
        lng: selectedCityLng,
        distance: formValues.distance || "25",
        page: 1,
        search: true,
      };
      setFilterData(searchData);
      getProviders(searchData);
      setHasSearched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationPermissionGranted,
    selectedCityLat,
    selectedCityLng,
    hasSearched,
  ]);

  // Handle filter changes and back navigation
  useEffect(() => {
    document.getElementById("root").scrollTo(0, 0);
    if (isClearingFilters || hasSearched || isPaginationNavigation) {
      return;
    }
    const providerViewed = sessionStorage.getItem("providerViewed") === "true";

    if (providerViewed && filterData?.isSearchResult) {
      Object.entries({
        userType: filterData?.type || "",
        name: filterData?.name || "",
        ratings: filterData?.ratings || "",
        address: filterData?.address || "",
        city: filterData?.city || "",
        mopRecommended: filterData?.mop || "",
        distance: filterData?.distance || "",
        page: filterData?.page || 1,
      }).forEach(([key, value]) => setValue(key, value));
      setSelectedCity(filterData.address || "");
      setSelectedCityLat(filterData.lat || "");
      setSelectedCityLng(filterData.lng || "");
      setRating(filterData.ratings || "");
      getProviders({
        page: filterData.page || 1,
        name: filterData.name || "",
        userType: filterData.type || "",
        ratings: filterData.ratings || "",
        address: filterData.address || "",
        city: filterData.city || "",
        mopRecommended: filterData.mop || "",
        lat: filterData.lat || "",
        lng: filterData.lng || "",
        distance: filterData.distance || "",
      });
      return;
    }

    if (
      filterData?.address ||
      filterData?.name ||
      filterData?.type ||
      filterData?.ratings ||
      filterData?.mop
    ) {
      const payload = {
        page: filterData?.page || 1,
        userType: filterData?.type || "",
        name: filterData?.name || "",
        rating: filterData?.ratings || "",
        address: filterData?.address || "",
        city: filterData?.city || "",
        mopRecommended: filterData?.mop || "",
        lat: selectedCityLat || filterData?.lat || "",
        lng: selectedCityLng || filterData?.lng || "",
        distance: filterData?.distance || "",
      };
      getProviders(payload);
      Object.entries({
        userType: filterData?.type || "",
        name: filterData?.name || "",
        ratings: filterData?.ratings || "",
        address: filterData?.address || "",
        city: filterData?.city || "",
        mopRecommended: filterData?.mop || "",
        distance: filterData?.distance || "",
        page: filterData?.page || 1,
      }).forEach(([key, value]) => setValue(key, value));
      if (filterData?.address) {
        setSelectedCity(filterData.address);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  const handleClearFilters = () => {
    setIsClearingFilters(true);
    setIsPaginationNavigation(false);
    setFilterData({});
    setRating("");
    setSelectedCityLat("");
    setSelectedCityLng("");
    setSelectedCity("");
    setCity([]);
    setDistanceError("");
    getProviders({ page: 1 });
    setIsSearchOpen(false);
    Object.entries({
      userType: "",
      name: "",
      ratings: "",
      address: "",
      city: "",
      mopRecommended: "",
      distance: "",
      page: 1,
    }).forEach(([key, value]) => setValue(key, value));
    setIsGettingLocation(false);
    setLocationError("");
    localStorage.removeItem("mop-location-permission");
    setLocationPermissionGranted(false);
    setHasSearched(false);
    sessionStorage.removeItem("providerViewed");
  };

  const handleDistanceChange = (e) => {
    setValue("distance", e.target.value);
    setDistanceError("");
    setCity([]);
  };

  return (
    <Layout>
      <section className="pb-3 bg-F5F5F7">
        <div className="container">
          <div className="row mb-3 pt-4">
            <div className="col-sm-12 d-block justify-content-between">
              <h2 className="text-black fw-bold">Find a Provider</h2>
              <h5 className="text-muted mb-3">
                Connect with a provider to get treated
              </h5>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4">
              <div
                className="mb-4 p-4"
                style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "solid 1px #EDEEF0",
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">Search</h5>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-sm btn-link text-primary text-decoration-none fw-bold p-0 no-hover d-none d-md-block"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </button>
                    <button
                      className="btn btn-sm btn-link text-success text-decoration-none fw-bold p-0 no-hover d-md-none"
                      onClick={() => setIsSearchOpen(!isSearchOpen)}
                      style={{ fontSize: "1rem" }}
                    >
                      {isSearchOpen ? "Close" : "Open"}
                      <i
                        className={`fas fa-chevron-${
                          isSearchOpen ? "up" : "down"
                        } ms-1`}
                      ></i>
                    </button>
                  </div>
                </div>
                <div
                  className={`${
                    isSearchOpen ? "d-block" : "d-none"
                  } d-md-block mt-3 pt-3`}
                >
                  <form
                    onSubmit={handleSubmit((formData) => {
                      if (
                        formData.distance &&
                        (!selectedCityLat || !selectedCityLng)
                      ) {
                        setDistanceError(
                          "Enter an address or city to search by distance"
                        );
                        return;
                      }
                      if (
                        selectedCityLat &&
                        selectedCityLng &&
                        !formData.distance
                      ) {
                        formData.distance = "25";
                        setValue("distance", "25");
                      }
                      const data = {
                        name: formData?.name || "",
                        type: formData?.userType || "",
                        ratings: rating,
                        address: formData?.address || "",
                        city: formData?.city || "",
                        mop: formData?.mopRecommended || "",
                        lat: selectedCityLat,
                        lng: selectedCityLng,
                        distance: formData?.distance || "",
                        page: 1,
                        back: false,
                        isSearchResult: true,
                      };
                      setFilterData(data);
                      getProviders({
                        ...formData,
                        page: 1,
                        search: true,
                        ratings: rating,
                        lat: selectedCityLat,
                        lng: selectedCityLng,
                      });
                      setIsSearchOpen(false);
                      setHasSearched(true);
                      setIsPaginationNavigation(false);
                    })}
                  >
                    <div
                      className="mb-4"
                      style={{ borderTop: "solid 1px #EDEEF0" }}
                    >
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control border-0"
                          style={{
                            backgroundColor: "#F2F8FF",
                            color: "black",
                            borderRadius: "6px",
                            padding: "10px",
                            paddingRight: "40px",
                          }}
                          placeholder="Location"
                          {...register("address", {
                            onChange: onChangeCityHandler,
                          })}
                          ref={(e) => {
                            register("address").ref(e);
                            addressInputRef.current = e;
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute location-button"
                          style={{
                            right: "5px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            padding: "0",
                            color: locationPermissionGranted
                              ? "#007bff"
                              : "#6c757d",
                            zIndex: 10,
                          }}
                          onClick={handleDirectLocationAccess}
                          disabled={isGettingLocation}
                          title={
                            locationPermissionGranted
                              ? "Use current location"
                              : "Location access denied"
                          }
                        >
                          {isGettingLocation ? (
                            <i
                              className="fas fa-spinner fa-spin"
                              style={{ float: "right", paddingRight: "5px" }}
                            ></i>
                          ) : (
                            <i
                              className="fas fa-location-dot"
                              style={{ float: "right", paddingRight: "5px" }}
                            ></i>
                          )}
                        </button>
                      </div>
                      {city.length > 0 && (
                        <div
                          className="dropdown-menu d-block provider-select-div"
                          ref={suggestionListRef}
                          style={{ zIndex: 1000 }}
                        >
                          <ul className="provider-select-ul">
                            <p
                              className="float-right cursor-pointer p-0 fw-bold"
                              onClick={() => setCity([])}
                            >
                              X
                            </p>
                            {city.map((data, i) => (
                              <li
                                key={i}
                                onClick={() => selectCityHandler(i)}
                                className="provider-select-li"
                              >
                                {data.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="mb-4">
                      <div className="position-relative">
                        <input
                          type="number"
                          className="form-control border-0"
                          style={{
                            backgroundColor: selectedCity
                              ? "#F2F8FF"
                              : "#E9ECEF",
                            color: selectedCity ? "black" : "#6C757D",
                            borderRadius: "6px",
                            padding: "10px",
                          }}
                          placeholder="Distance"
                          {...register("distance")}
                          onChange={handleDistanceChange}
                          onFocus={() => setCity([])}
                          value={distanceValue || ""}
                          disabled={!selectedCity}
                        />
                        {locationError && (
                          <div className="text-danger small mt-1">
                            {locationError}
                          </div>
                        )}
                      </div>
                      {distanceError && (
                        <div className="text-danger small mt-1">
                          {distanceError}
                        </div>
                      )}
                    </div>
                    <div className="mb-4">
                      <input
                        type="text"
                        className="form-control border-0"
                        style={{
                          backgroundColor: "#F2F8FF",
                          color: "black",
                          borderRadius: "6px",
                          padding: "10px",
                        }}
                        placeholder="Provider Name"
                        {...register("name")}
                        onFocus={() => setCity([])}
                      />
                    </div>
                    <div className="mb-4">
                      <select
                        className={`form-select border-0 ${
                          !watch("userType") ? "text-muted" : "text-black"
                        }`}
                        style={{
                          backgroundColor: "#F2F8FF",
                          borderRadius: "6px",
                          padding: "10px",
                        }}
                        {...register("userType", {
                          onChange: (e) => setValue("userType", e.target.value),
                        })}
                        onFocus={() => setCity([])}
                      >
                        <option value="" disabled hidden>
                          Enter Provider Type
                        </option>
                        <option value="">Enter Provider Type</option>
                        {(brand.key === "mdp" ? DentalProvidersListV4 : ProvidersListV4)?.map((providerType) => (
                          <option value={providerType?.value} key={providerType?.value}>
                            {providerType?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary rounded-lg flex-grow-1 py-2 fw-bold"
                        type="submit"
                        disabled={isGettingLocation}
                      >
                        {isGettingLocation ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Search
                          </>
                        ) : (
                          "Search"
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary rounded-lg py-2 fw-bold d-md-none"
                        onClick={handleClearFilters}
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="col-lg-8 mt-lg-0">
              {isGettingLocation || loader ? (
                <LoadingScreen color="primary" />
              ) : (
                <>
                  <div className="mb-4 mt-2">
                    {providers && providers.length > 0 ? (
                      providers.map((provider) => (
                        <ProviderCard
                          key={provider._id}
                          provider={provider}
                          evaluation={location.state?.evaluation}
                          condition={location.state?.condition}
                        />
                      ))
                    ) : (
                      <h4 className="text-center">No data Found</h4>
                    )}
                  </div>
                  {!loader && providers && providers.length > 0 && (
                    <Pagination
                      onPageChange={(event) => {
                        setProviderPagination({
                          totalPages: totalPages,
                          currentPageNo: event.selected + 1,
                        });
                        handlePageChange(event.selected + 1);
                      }}
                      pageRangeDisplayed={3}
                      totalPages={totalPages}
                      currentPageNo={currentPageNo - 1}
                      isMobile={window.innerWidth < 769}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Providers;
